import { FaceCategory } from "building-types/materials/faceCategories";
import * as THREE from "three";
import type { EffectComposerPassInterface } from "../examples/jsm/postprocessing/effectComposerTypes";

export type DrawCallAttributionPass =
  | "physical"
  | "depth-normal"
  | "ambient-occlusion"
  | "reflections"
  | "screen-space-shadow"
  | "temporal-accumulation"
  | "output"
  | "other";

export interface DrawCallAttributionPassSnapshot {
  readonly totalDrawCalls: number;
  /** Submitted vertex/index elements, multiplied by the actual instance count; not unique vertices. */
  readonly submittedElements: number;
  /** Triangle submissions, including repeated passes/instances; not visible or rasterized triangles. */
  readonly submittedTriangles: number;
  readonly submittedTrianglesByFaceCategory: Readonly<Record<string, number>>;
  readonly instancedDrawCalls: number;
  readonly uniqueObjectCount: number;
  readonly uniqueInstancedObjectCount: number;
  readonly byFaceCategory: Readonly<Record<string, number>>;
  readonly instancedByFaceCategory: Readonly<Record<string, number>>;
  readonly uniqueObjectsByFaceCategory: Readonly<Record<string, number>>;
  readonly mergeCompatibility: DrawCallMergeCompatibilitySnapshot;
}

export interface DrawCallMergeCompatibilityCategorySnapshot {
  readonly regularObjectCount: number;
  readonly eligibleObjectCount: number;
  readonly compatibilityBucketCount: number;
  readonly projectedObjectCount: number;
  readonly potentialReduction: number;
  readonly uniqueMaterialCount: number;
  readonly uniqueParentCount: number;
  readonly uniqueUserDataCount: number;
  readonly geometryLayoutCount: number;
}

export interface DrawCallMergeCompatibilitySnapshot {
  readonly regularObjectCount: number;
  readonly eligibleObjectCount: number;
  readonly compatibilityBucketCount: number;
  readonly projectedObjectCount: number;
  readonly potentialReduction: number;
  readonly byFaceCategory: Readonly<Record<string, DrawCallMergeCompatibilityCategorySnapshot>>;
}

export interface DrawCallAttributionSnapshot {
  readonly totalDrawCalls: number;
  readonly submittedElements: number;
  readonly submittedTriangles: number;
  readonly byPass: Readonly<Partial<Record<DrawCallAttributionPass, DrawCallAttributionPassSnapshot>>>;
  readonly frustumCulling?: FrustumCullingAttributionSnapshot;
  readonly boxCulling?: BoxCullingAttributionSnapshot;
}

export interface DrawCallAttributionOptions {
  readonly analyzeBoxCulling?: boolean;
}

export interface BoxCullingAttributionStats {
  readonly boxEligibleDraws: number;
  readonly sphereAcceptedCandidateDraws: number;
  readonly uniqueEligibleObjectCount: number;
  readonly uniqueCandidateObjectCount: number;
  /** Actual submissions of sphere-accepted objects excluded from the hypothesis. */
  readonly skippedReasons: Readonly<Record<string, number>>;
}

export interface BoxCullingAttributionSnapshot extends BoxCullingAttributionStats {
  readonly hypothesis: string;
  readonly byPass: Readonly<Partial<Record<DrawCallAttributionPass, BoxCullingAttributionStats>>>;
  readonly byFaceCategory: Readonly<Record<string, BoxCullingAttributionStats>>;
}

interface MutableBoxCullingStats {
  boxEligibleDraws: number;
  sphereAcceptedCandidateDraws: number;
  readonly eligibleObjects: Set<THREE.Object3D>;
  readonly candidateObjects: Set<THREE.Object3D>;
  readonly skippedReasons: Record<string, number>;
}

interface CachedPositionBox {
  readonly position: THREE.BufferAttribute | THREE.InterleavedBufferAttribute;
  readonly array: ArrayLike<number>;
  readonly version: number;
  readonly box: THREE.Box3;
}

export interface FrustumCullingAttributionStats {
  readonly tests: number;
  readonly accepted: number;
  readonly rejected: number;
  readonly uniqueObjectCount: number;
  readonly uniqueRejectedObjectCount: number;
}

export interface FrustumCullingAttributionSnapshot extends FrustumCullingAttributionStats {
  readonly byPass: Readonly<Partial<Record<DrawCallAttributionPass, FrustumCullingAttributionStats>>>;
  readonly byFaceCategory: Readonly<Record<string, FrustumCullingAttributionStats>>;
}

export interface DrawCallAttributionPassScope {
  readonly pass: DrawCallAttributionPass;
  readonly effect: EffectComposerPassInterface;
}

interface MutablePassStats {
  totalDrawCalls: number;
  submittedElements: number;
  submittedTriangles: number;
  readonly submittedTrianglesByFaceCategory: Map<string, number>;
  instancedDrawCalls: number;
  readonly objects: Set<THREE.Object3D>;
  readonly instancedObjects: Set<THREE.Object3D>;
  readonly byFaceCategory: Map<string, number>;
  readonly instancedByFaceCategory: Map<string, number>;
  readonly objectsByFaceCategory: Map<string, Set<THREE.Object3D>>;
}

interface MutableFrustumCullingStats {
  tests: number;
  accepted: number;
  rejected: number;
  readonly objects: Set<THREE.Object3D>;
  readonly rejectedObjects: Set<THREE.Object3D>;
}

const UNCATEGORIZED = "UNCATEGORIZED";

interface MutableMergeCompatibilityCategoryStats {
  regularObjectCount: number;
  eligibleObjectCount: number;
  readonly compatibilityKeys: Set<string>;
  readonly materials: Set<THREE.Material>;
  readonly parents: Set<THREE.Object3D | null>;
  readonly userDataKeys: Set<string>;
  readonly geometryLayoutKeys: Set<string>;
}

const semanticMaterials = new WeakMap<THREE.Mesh, THREE.Material | THREE.Material[]>();

type FrustumTestRecorder = (object: THREE.Object3D, accepted: boolean, frustum: THREE.Frustum) => void;

interface FrustumTestRecorderScope {
  readonly recorder: FrustumTestRecorder;
}

const frustumTestRecorderScopes: FrustumTestRecorderScope[] = [];
const collectorRenderers = new WeakSet<THREE.WebGLRenderer>();
let originalIntersectsObject: THREE.Frustum["intersectsObject"] | undefined;
let wrappedIntersectsObject: THREE.Frustum["intersectsObject"] | undefined;

function registerFrustumTestRecorder(recorder: FrustumTestRecorder): () => void {
  if (frustumTestRecorderScopes.length === 0) {
    const intersectsObject = THREE.Frustum.prototype.intersectsObject;
    originalIntersectsObject = intersectsObject;
    wrappedIntersectsObject = function (object): boolean {
      const accepted = intersectsObject.call(this, object);
      // One collector per renderer makes the innermost scope sufficient, even for recursive renders.
      frustumTestRecorderScopes.at(-1)?.recorder(object, accepted, this);
      return accepted;
    };
    THREE.Frustum.prototype.intersectsObject = wrappedIntersectsObject;
  }
  const scope: FrustumTestRecorderScope = { recorder };
  frustumTestRecorderScopes.push(scope);

  return () => {
    const scopeIndex = frustumTestRecorderScopes.lastIndexOf(scope);
    if (scopeIndex !== -1) {
      frustumTestRecorderScopes.splice(scopeIndex, 1);
    }
    if (frustumTestRecorderScopes.length !== 0) {
      return;
    }
    if (originalIntersectsObject && THREE.Frustum.prototype.intersectsObject === wrappedIntersectsObject) {
      THREE.Frustum.prototype.intersectsObject = originalIntersectsObject;
    }
    originalIntersectsObject = undefined;
    wrappedIntersectsObject = undefined;
  };
}

/** Record the stable material identity to use when UI state temporarily overrides a mesh material. */
export function setDrawCallAttributionSemanticMaterial(
  mesh: THREE.Mesh,
  material: THREE.Material | THREE.Material[],
): void {
  semanticMaterials.set(mesh, material);
}

/** Return a mesh's stable material identity when one has been recorded. */
export function getDrawCallAttributionSemanticMaterial(
  mesh: THREE.Mesh,
): THREE.Material | THREE.Material[] | undefined {
  return semanticMaterials.get(mesh);
}

function isInstancedObject(object: THREE.Object3D): boolean {
  return "isInstancedMesh" in object && object.isInstancedMesh === true;
}

function isMesh(object: THREE.Object3D): object is THREE.Mesh {
  return "isMesh" in object && object.isMesh === true;
}

function geometryLayoutKey(geometry: THREE.BufferGeometry): string | undefined {
  if (
    Object.keys(geometry.morphAttributes).length > 0 ||
    geometry.groups.length > 0 ||
    geometry.drawRange.start !== 0 ||
    geometry.drawRange.count !== Infinity
  ) {
    return;
  }

  const attributes = Object.entries(geometry.attributes).sort(([left], [right]) => left.localeCompare(right));
  if (attributes.some(([, attribute]) => "isInterleavedBufferAttribute" in attribute)) {
    return;
  }
  return JSON.stringify({
    indexed: geometry.index !== null,
    morphTargetsRelative: geometry.morphTargetsRelative,
    attributes: attributes.map(([name, attribute]) => ({
      name,
      arrayType: attribute.array.constructor.name,
      itemSize: attribute.itemSize,
      normalized: attribute.normalized,
    })),
  });
}

function jsonKey(value: unknown): string | undefined {
  try {
    return JSON.stringify(value);
  } catch {
    return;
  }
}

function objectIdentity(object: object | null | undefined, identities: Map<object, number>): number {
  if (!object) {
    return 0;
  }
  const existing = identities.get(object);
  if (existing !== undefined) {
    return existing;
  }
  const identity = identities.size + 1;
  identities.set(object, identity);
  return identity;
}

function mergeCompatibilitySnapshot(objects: ReadonlySet<THREE.Object3D>): DrawCallMergeCompatibilitySnapshot {
  const identities = new Map<object, number>();
  const categories = new Map<string, MutableMergeCompatibilityCategoryStats>();
  const allCompatibilityKeys = new Set<string>();
  let regularObjectCount = 0;
  let eligibleObjectCount = 0;

  for (const object of objects) {
    if (!isMesh(object) || isInstancedObject(object)) {
      continue;
    }
    regularObjectCount += 1;
    const category = faceCategoryName(object);
    let stats = categories.get(category);
    if (!stats) {
      stats = {
        regularObjectCount: 0,
        eligibleObjectCount: 0,
        compatibilityKeys: new Set(),
        materials: new Set(),
        parents: new Set(),
        userDataKeys: new Set(),
        geometryLayoutKeys: new Set(),
      };
      categories.set(category, stats);
    }
    stats.regularObjectCount += 1;

    const material = getDrawCallAttributionSemanticMaterial(object) ?? object.material;
    const layoutKey = geometryLayoutKey(object.geometry);
    const userDataKey = jsonKey(object.userData);
    if (Array.isArray(material) || layoutKey === undefined || userDataKey === undefined || "isSkinnedMesh" in object) {
      continue;
    }

    eligibleObjectCount += 1;
    stats.eligibleObjectCount += 1;
    stats.materials.add(material);
    stats.parents.add(object.parent);
    stats.userDataKeys.add(userDataKey);
    stats.geometryLayoutKeys.add(layoutKey);
    const compatibilityKey = JSON.stringify([
      category,
      objectIdentity(material, identities),
      objectIdentity(object.parent, identities),
      objectIdentity(object.customDepthMaterial, identities),
      objectIdentity(object.customDistanceMaterial, identities),
      object.layers.mask,
      object.castShadow,
      object.receiveShadow,
      object.frustumCulled,
      object.renderOrder,
      userDataKey,
      layoutKey,
    ]);
    stats.compatibilityKeys.add(compatibilityKey);
    allCompatibilityKeys.add(compatibilityKey);
  }

  const byFaceCategory: Record<string, DrawCallMergeCompatibilityCategorySnapshot> = {};
  for (const [category, stats] of [...categories.entries()].sort(([left], [right]) => left.localeCompare(right))) {
    const potentialReduction = stats.eligibleObjectCount - stats.compatibilityKeys.size;
    byFaceCategory[category] = {
      regularObjectCount: stats.regularObjectCount,
      eligibleObjectCount: stats.eligibleObjectCount,
      compatibilityBucketCount: stats.compatibilityKeys.size,
      projectedObjectCount: stats.regularObjectCount - potentialReduction,
      potentialReduction,
      uniqueMaterialCount: stats.materials.size,
      uniqueParentCount: stats.parents.size,
      uniqueUserDataCount: stats.userDataKeys.size,
      geometryLayoutCount: stats.geometryLayoutKeys.size,
    };
  }

  const potentialReduction = eligibleObjectCount - allCompatibilityKeys.size;
  return {
    regularObjectCount,
    eligibleObjectCount,
    compatibilityBucketCount: allCompatibilityKeys.size,
    projectedObjectCount: regularObjectCount - potentialReduction,
    potentialReduction,
    byFaceCategory,
  };
}

function faceCategoryName(object: THREE.Object3D | undefined): string {
  const faceCategory: unknown = object?.userData.faceCategory;
  if (typeof faceCategory !== "number") {
    return UNCATEGORIZED;
  }
  return FaceCategory[faceCategory] ?? `UNKNOWN_FACE_CATEGORY_${faceCategory}`;
}

function unscopedPass(material: THREE.Material | undefined): DrawCallAttributionPass {
  if (material?.name.startsWith("SSAAPass addSample") === true) {
    return "temporal-accumulation";
  }
  if (material?.name === "SSAAPass outputMaterial") {
    return "output";
  }
  return "other";
}

function createMutableFrustumCullingStats(): MutableFrustumCullingStats {
  return {
    tests: 0,
    accepted: 0,
    rejected: 0,
    objects: new Set(),
    rejectedObjects: new Set(),
  };
}

function frustumCullingStatsSnapshot(stats: MutableFrustumCullingStats): FrustumCullingAttributionStats {
  return {
    tests: stats.tests,
    accepted: stats.accepted,
    rejected: stats.rejected,
    uniqueObjectCount: stats.objects.size,
    uniqueRejectedObjectCount: stats.rejectedObjects.size,
  };
}

function createBoxCullingStats(): MutableBoxCullingStats {
  return {
    boxEligibleDraws: 0,
    sphereAcceptedCandidateDraws: 0,
    eligibleObjects: new Set(),
    candidateObjects: new Set(),
    skippedReasons: {},
  };
}

function boxCullingStatsSnapshot(stats: MutableBoxCullingStats): BoxCullingAttributionStats {
  return {
    boxEligibleDraws: stats.boxEligibleDraws,
    sphereAcceptedCandidateDraws: stats.sphereAcceptedCandidateDraws,
    uniqueEligibleObjectCount: stats.eligibleObjects.size,
    uniqueCandidateObjectCount: stats.candidateObjects.size,
    skippedReasons: { ...stats.skippedReasons },
  };
}

function boxMaterialSkip(material: THREE.Material): string | undefined {
  if ("isShaderMaterial" in material || "isRawShaderMaterial" in material) {
    return "shader-material";
  }
  if ("displacementMap" in material && material.displacementMap) {
    return "displacement-map";
  }
  return undefined;
}

/** Opt-in draw-submission attribution for renderer benchmarks. */
export class DrawCallAttributionCollector {
  private currentObject: THREE.Object3D | undefined;
  private currentMaterial: THREE.Material | undefined;
  private currentPass: DrawCallAttributionPass | undefined;
  private readonly passStats = new Map<DrawCallAttributionPass, MutablePassStats>();
  private frustumCullingStats = createMutableFrustumCullingStats();
  private readonly frustumCullingByPass = new Map<DrawCallAttributionPass, MutableFrustumCullingStats>();
  private readonly frustumCullingByFaceCategory = new Map<string, MutableFrustumCullingStats>();
  // Membership is local to renderer.render; true means sphere admitted but the world box rejected.
  private boxResults: Map<THREE.Object3D, boolean | string> | undefined;
  private readonly positionBoxes = new WeakMap<THREE.BufferGeometry, CachedPositionBox>();
  private boxStats = createBoxCullingStats();
  private readonly boxByPass = new Map<DrawCallAttributionPass, MutableBoxCullingStats>();
  private readonly boxByFaceCategory = new Map<string, MutableBoxCullingStats>();
  private readonly restoreCallbacks: Array<() => void> = [];
  private disposed = false;

  constructor(
    private readonly renderer: THREE.WebGLRenderer,
    passScopes: readonly DrawCallAttributionPassScope[],
    private readonly options: DrawCallAttributionOptions = {},
  ) {
    if (collectorRenderers.has(renderer)) {
      throw new Error("A draw-call attribution collector is already active for this renderer");
    }
    collectorRenderers.add(renderer);
    try {
      this.wrapRenderer();
      this.wrapRendererRenderScope();
      for (const scope of passScopes) {
        this.wrapPass(scope);
      }
    } catch (error: unknown) {
      collectorRenderers.delete(renderer);
      throw error;
    }
  }

  snapshot(): DrawCallAttributionSnapshot {
    const byPass: Partial<Record<DrawCallAttributionPass, DrawCallAttributionPassSnapshot>> = {};
    let totalDrawCalls = 0;
    let submittedElements = 0;
    let submittedTriangles = 0;
    for (const [pass, stats] of this.passStats) {
      totalDrawCalls += stats.totalDrawCalls;
      submittedElements += stats.submittedElements;
      submittedTriangles += stats.submittedTriangles;
      byPass[pass] = {
        totalDrawCalls: stats.totalDrawCalls,
        submittedElements: stats.submittedElements,
        submittedTriangles: stats.submittedTriangles,
        submittedTrianglesByFaceCategory: Object.fromEntries(
          [...stats.submittedTrianglesByFaceCategory].sort(([left], [right]) => left.localeCompare(right)),
        ),
        instancedDrawCalls: stats.instancedDrawCalls,
        uniqueObjectCount: stats.objects.size,
        uniqueInstancedObjectCount: stats.instancedObjects.size,
        byFaceCategory: Object.fromEntries(
          [...stats.byFaceCategory.entries()].sort(([left], [right]) => left.localeCompare(right)),
        ),
        instancedByFaceCategory: Object.fromEntries(
          [...stats.instancedByFaceCategory.entries()].sort(([left], [right]) => left.localeCompare(right)),
        ),
        uniqueObjectsByFaceCategory: Object.fromEntries(
          [...stats.objectsByFaceCategory.entries()]
            .sort(([left], [right]) => left.localeCompare(right))
            .map(([category, objects]) => [category, objects.size]),
        ),
        mergeCompatibility: mergeCompatibilitySnapshot(stats.objects),
      };
    }
    const boxCulling = this.options.analyzeBoxCulling
      ? {
          boxCulling: {
            hypothesis:
              "Conservative raw-position-bound hypothesis only; unknown vertex shader/onBeforeCompile effects, not production eligibility or performance evidence.",
            ...boxCullingStatsSnapshot(this.boxStats),
            byPass: Object.fromEntries(
              [...this.boxByPass].map(([key, stats]) => [key, boxCullingStatsSnapshot(stats)]),
            ),
            byFaceCategory: Object.fromEntries(
              [...this.boxByFaceCategory].map(([key, stats]) => [key, boxCullingStatsSnapshot(stats)]),
            ),
          },
        }
      : {};
    if (this.frustumCullingStats.tests === 0) {
      return { totalDrawCalls, submittedElements, submittedTriangles, byPass, ...boxCulling };
    }
    return {
      totalDrawCalls,
      submittedElements,
      submittedTriangles,
      byPass,
      ...boxCulling,
      frustumCulling: {
        ...frustumCullingStatsSnapshot(this.frustumCullingStats),
        byPass: Object.fromEntries(
          [...this.frustumCullingByPass.entries()].map(([pass, stats]) => [pass, frustumCullingStatsSnapshot(stats)]),
        ),
        byFaceCategory: Object.fromEntries(
          [...this.frustumCullingByFaceCategory.entries()]
            .sort(([left], [right]) => left.localeCompare(right))
            .map(([category, stats]) => [category, frustumCullingStatsSnapshot(stats)]),
        ),
      },
    };
  }

  reset(): void {
    this.passStats.clear();
    this.frustumCullingStats = createMutableFrustumCullingStats();
    this.frustumCullingByPass.clear();
    this.frustumCullingByFaceCategory.clear();
    this.boxStats = createBoxCullingStats();
    this.boxByPass.clear();
    this.boxByFaceCategory.clear();
  }

  dispose(): void {
    if (this.disposed) {
      return;
    }
    this.disposed = true;
    collectorRenderers.delete(this.renderer);
    for (const restore of this.restoreCallbacks.reverse()) {
      restore();
    }
    this.restoreCallbacks.length = 0;
  }

  private wrapRendererRenderScope(): void {
    const originalRender = this.renderer.render;
    const wrappedRender: THREE.WebGLRenderer["render"] = (scene, camera) => {
      const previousBoxResults = this.boxResults;
      this.boxResults = this.options.analyzeBoxCulling ? new Map() : undefined;
      const unregister = registerFrustumTestRecorder(this.frustumTestRecorder);
      try {
        originalRender.call(this.renderer, scene, camera);
      } finally {
        unregister();
        this.boxResults = previousBoxResults;
      }
    };
    this.renderer.render = wrappedRender;
    this.restoreCallbacks.push(() => {
      if (this.renderer.render === wrappedRender) {
        this.renderer.render = originalRender;
      }
    });
  }

  private readonly frustumTestRecorder: FrustumTestRecorder = (object, accepted, frustum) => {
    this.recordFrustumTest(object, accepted);
    this.boxResults?.delete(object);
    if (accepted && this.boxResults) {
      this.boxResults.set(object, this.testPositionBox(object, frustum));
    }
  };

  private testPositionBox(object: THREE.Object3D, frustum: THREE.Frustum): boolean | string {
    if (!isMesh(object) || isInstancedObject(object) || "isSkinnedMesh" in object || "isBatchedMesh" in object) {
      return "non-ordinary-mesh";
    }
    const { geometry } = object;
    if (Object.keys(geometry.morphAttributes).length > 0 || (object.morphTargetInfluences?.length ?? 0) > 0) {
      return "morph-targets";
    }
    const materials = Array.isArray(object.material) ? object.material : [object.material];
    for (const material of materials) {
      const skip = boxMaterialSkip(material);
      if (skip) {
        return skip;
      }
    }
    const position = geometry.getAttribute("position");
    if (!position || !position.array || position.itemSize < 3 || position.count === 0) {
      return "unreadable-position";
    }
    const version = position instanceof THREE.InterleavedBufferAttribute ? position.data.version : position.version;
    let cached = this.positionBoxes.get(geometry);
    if (!cached || cached.position !== position || cached.array !== position.array || cached.version !== version) {
      const box = new THREE.Box3();
      const point = new THREE.Vector3();
      for (let i = 0; i < position.count; i++) {
        box.expandByPoint(point.set(position.getX(i), position.getY(i), position.getZ(i)));
      }
      cached = { position, array: position.array, version, box };
      this.positionBoxes.set(geometry, cached);
    }
    if (!object.matrixWorld.elements.every(Number.isFinite)) {
      return "non-finite-bounds-or-matrix";
    }
    const box = new THREE.Box3().copy(cached.box).applyMatrix4(object.matrixWorld);
    if (
      ![...cached.box.min.toArray(), ...cached.box.max.toArray(), ...box.min.toArray(), ...box.max.toArray()].every(
        Number.isFinite,
      )
    ) {
      return "non-finite-bounds-or-matrix";
    }
    return !frustum.intersectsBox(box);
  }

  private wrapRenderer(): void {
    const originalRenderBufferDirect = this.renderer.renderBufferDirect;
    const wrappedRenderBufferDirect: THREE.WebGLRenderer["renderBufferDirect"] = (
      camera,
      scene,
      geometry,
      material,
      object,
      group,
    ) => {
      const previousObject = this.currentObject;
      const previousMaterial = this.currentMaterial;
      this.currentObject = object;
      this.currentMaterial = material;
      try {
        return originalRenderBufferDirect.call(this.renderer, camera, scene, geometry, material, object, group);
      } finally {
        this.currentObject = previousObject;
        this.currentMaterial = previousMaterial;
      }
    };
    this.renderer.renderBufferDirect = wrappedRenderBufferDirect;
    this.restoreCallbacks.push(() => {
      if (this.renderer.renderBufferDirect === wrappedRenderBufferDirect) {
        this.renderer.renderBufferDirect = originalRenderBufferDirect;
      }
    });

    const originalUpdate = this.renderer.info.update;
    const wrappedUpdate: THREE.WebGLRenderer["info"]["update"] = (count, mode, instanceCount) => {
      this.recordDrawCall(count, mode, instanceCount);
      originalUpdate.call(this.renderer.info, count, mode, instanceCount);
    };
    this.renderer.info.update = wrappedUpdate;
    this.restoreCallbacks.push(() => {
      if (this.renderer.info.update === wrappedUpdate) {
        this.renderer.info.update = originalUpdate;
      }
    });
  }

  private wrapPass({ effect, pass }: DrawCallAttributionPassScope): void {
    const originalRender = effect.render;
    const wrappedRender: EffectComposerPassInterface["render"] = (renderer, writeBuffer) => {
      const previousPass = this.currentPass;
      this.currentPass = pass;
      try {
        originalRender.call(effect, renderer, writeBuffer);
      } finally {
        this.currentPass = previousPass;
      }
    };
    effect.render = wrappedRender;
    this.restoreCallbacks.push(() => {
      if (effect.render === wrappedRender) {
        effect.render = originalRender;
      }
    });
  }

  private recordDrawCall(count: number, mode: number, instanceCount: number): void {
    const pass = this.currentPass ?? unscopedPass(this.currentMaterial);
    let stats = this.passStats.get(pass);
    if (!stats) {
      stats = {
        totalDrawCalls: 0,
        submittedElements: 0,
        submittedTriangles: 0,
        submittedTrianglesByFaceCategory: new Map(),
        instancedDrawCalls: 0,
        objects: new Set(),
        instancedObjects: new Set(),
        byFaceCategory: new Map(),
        instancedByFaceCategory: new Map(),
        objectsByFaceCategory: new Map(),
      };
      this.passStats.set(pass, stats);
    }
    const category = faceCategoryName(this.currentObject);
    const elements = count * instanceCount;
    // WebGL TRIANGLES is 0x0004, not THREE.TrianglesDrawMode (which is a geometry draw-mode enum).
    const triangles = mode === 0x0004 ? elements / 3 : 0;
    stats.submittedElements += elements;
    stats.submittedTriangles += triangles;
    stats.submittedTrianglesByFaceCategory.set(
      category,
      (stats.submittedTrianglesByFaceCategory.get(category) ?? 0) + triangles,
    );
    const boxResult = this.currentObject && this.boxResults?.get(this.currentObject);
    if (this.currentObject && boxResult !== undefined) {
      const passBoxStats = this.boxByPass.get(pass) ?? createBoxCullingStats();
      const categoryBoxStats = this.boxByFaceCategory.get(category) ?? createBoxCullingStats();
      this.boxByPass.set(pass, passBoxStats);
      this.boxByFaceCategory.set(category, categoryBoxStats);
      const skip =
        typeof boxResult === "string" ? boxResult : this.currentMaterial && boxMaterialSkip(this.currentMaterial);
      for (const boxStats of [this.boxStats, passBoxStats, categoryBoxStats]) {
        if (skip) {
          boxStats.skippedReasons[skip] = (boxStats.skippedReasons[skip] ?? 0) + 1;
        } else {
          boxStats.boxEligibleDraws += 1;
          boxStats.eligibleObjects.add(this.currentObject);
          if (boxResult === true) {
            boxStats.sphereAcceptedCandidateDraws += 1;
            boxStats.candidateObjects.add(this.currentObject);
          }
        }
      }
    }
    stats.totalDrawCalls += 1;
    stats.byFaceCategory.set(category, (stats.byFaceCategory.get(category) ?? 0) + 1);
    if (this.currentObject) {
      stats.objects.add(this.currentObject);
      let categoryObjects = stats.objectsByFaceCategory.get(category);
      if (!categoryObjects) {
        categoryObjects = new Set();
        stats.objectsByFaceCategory.set(category, categoryObjects);
      }
      categoryObjects.add(this.currentObject);
    }
    if (this.currentObject && isInstancedObject(this.currentObject)) {
      stats.instancedDrawCalls += 1;
      stats.instancedObjects.add(this.currentObject);
      stats.instancedByFaceCategory.set(category, (stats.instancedByFaceCategory.get(category) ?? 0) + 1);
    }
  }

  private recordFrustumTest(object: THREE.Object3D, accepted: boolean): void {
    const record = (stats: MutableFrustumCullingStats): void => {
      stats.tests += 1;
      stats.objects.add(object);
      if (accepted) {
        stats.accepted += 1;
      } else {
        stats.rejected += 1;
        stats.rejectedObjects.add(object);
      }
    };

    record(this.frustumCullingStats);
    const pass = this.currentPass ?? "other";
    let passStats = this.frustumCullingByPass.get(pass);
    if (!passStats) {
      passStats = createMutableFrustumCullingStats();
      this.frustumCullingByPass.set(pass, passStats);
    }
    record(passStats);

    const category = faceCategoryName(object);
    let categoryStats = this.frustumCullingByFaceCategory.get(category);
    if (!categoryStats) {
      categoryStats = createMutableFrustumCullingStats();
      this.frustumCullingByFaceCategory.set(category, categoryStats);
    }
    record(categoryStats);
  }
}
